import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { DataSection, PitchData, PitchingRankingStats, RankingScopeContext, ResultDataNew, ScoringData } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import MultiRankingStatCalculator from "./../MultiRankingStatCalculator.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import ResultsSlice from "../../../valueObjects/ResultsSlice.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import resultsSliceForPitcher from "../../../classificators/resultsSlice/resultsSliceForPitcher.js";
import RankingStatCalculatorBase from "../RankingStatCalculatorBase.js";
import NewStrikeoutsCalculator from "./NewStrikeoutsCalculator.js";
import NewBaseHitsCalculator from "./NewBaseHitsCalculator.js";
import NewBaseOnBallsCalculator from "./NewBaseOnBallsCalculator.js";

class NewInningsPitchedCalculator extends MultiRankingStatCalculator{
    constructor(
        customResultsProcessor, rankingStatCaseVerifier, numberFormatter,
        strikeoutCalculator, hitsCalculator, walksCalculator,
        skipCache
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(strikeoutCalculator) !== 'undefined' && !(strikeoutCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('strikeoutCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }
        if (typeof(hitsCalculator) !== 'undefined' && !(hitsCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('hitsCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }
        if (typeof(walksCalculator) !== 'undefined' && !(walksCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('walksCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }

        const caseVerifier = rankingStatCaseVerifier || new RankingStatCaseVerifier(
            anyLeague,
            sessionWithStatGrabScoring,
            satisfiesAll(
                pitchingScopeContext,
                resultsSliceForPitcher
            )
        );

        super(
            [ 
                PitchingRankingStats.OutsPitched,
                PitchingRankingStats.InningsPitched,
                PitchingRankingStats.StrikeoutsPerNineInnings,
                PitchingRankingStats.WalksPerNineInnings,
                PitchingRankingStats.HomeRunsPerNineInnings,
                PitchingRankingStats.HitsPerNineInnings,
                PitchingRankingStats.WalksPlusHitsPerInningPitched,
            ],
            caseVerifier,
            skipCache
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );

        this.strikeoutCalculator = strikeoutCalculator || new NewStrikeoutsCalculator(
            RankingScopeContext.Pitching.key, this.resultsProcessor, caseVerifier, undefined, skipCache
        );
        this.hitsCalculator = hitsCalculator || new NewBaseHitsCalculator(
            RankingScopeContext.Pitching.key, this.resultsProcessor, caseVerifier, undefined, skipCache
        );
        this.walksCalculator = walksCalculator || new NewBaseOnBallsCalculator(
            RankingScopeContext.Pitching.key, this.resultsProcessor, caseVerifier, undefined, skipCache
        );
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        const results = slice.getSmallestSlice();
        const resultsPerInning = this.resultsProcessor.groupData(
            results,
            [ResultDataNew.getDataPath(PitchData.BeforePlayInning.key, DataSection.Scoring.key)]
        );

        let outsPitched = 0;
        const inningIsTopPath = ResultDataNew.getDataPath(PitchData.BeforePlayInningIsTop.key, DataSection.Scoring.key);
        const outsPath = ResultDataNew.getDataPath(ScoringData.Outs.key, DataSection.Scoring.key);
        for (const inningNr in resultsPerInning) {
            if (Number.isNaN(Number.parseFloat(inningNr))) {
                // 'undefined' can appear in some not scored data...
                continue;
            }

            const resultsInInning = resultsPerInning[inningNr];
            let prevInningIsTop = null;

            for (let i = 0; i < resultsInInning.length; i++) {
                const result = resultsInInning[i];

                const currentInningIsTop = this.resultsProcessor.getObjectContent(
                    result, inningIsTopPath
                ) == 1;

                if (prevInningIsTop === null) {
                    prevInningIsTop = currentInningIsTop;
                }

                if (prevInningIsTop != currentInningIsTop) {
                    throw new Error(`Data inconsistency detected on inning ${inningNr}, result ${result[ResultDataNew.Id.key]} - inning half changed while pitcher remains the same.`);
                }

                const outsOnThisPitch = this.resultsProcessor.getObjectContent(
                    result, outsPath
                )

                if (!(outsOnThisPitch instanceof Array)) {
                    continue;
                }

                outsPitched += outsOnThisPitch.length;
            }
        }

        if (!outsPitched) {
            // cant divide by 0, innings pitched is meaningless => 0
            return null;
        }

        const output = {};

        // TODO - throw or return nulls ?
        if (!this.strikeoutCalculator.canCalculate(
            PitchingRankingStats.Strikeouts, slice, session, league
        )) {
            throw new Error(`strikeoutCalculator is not able to calculate necessary stats - ${PitchingRankingStats.Strikeouts.key}`);
        }
        if (!this.hitsCalculator.canCalculate(
            PitchingRankingStats.HitsAllowed, slice, session, league
        )) {
            throw new Error(`hitsCalculator is not able to calculate necessary stats - ${PitchingRankingStats.HitsAllowed.key}`);
        }
        if (!this.hitsCalculator.canCalculate(
            PitchingRankingStats.HomeRunsAllowed, slice, session, league
        )) {
            throw new Error(`hitsCalculator is not able to calculate necessary stats - ${PitchingRankingStats.HomeRunsAllowed.key}`);
        }
        if (!this.walksCalculator.canCalculate(
            PitchingRankingStats.WalksAllowed, slice, session, league
        )) {
            throw new Error(`walksCalculator is not able to calculate necessary stats - ${PitchingRankingStats.WalksAllowed.key}`);
        }

        const strikeouts = this.strikeoutCalculator.calculate(
            PitchingRankingStats.Strikeouts, slice, session, league
        );
        const walks = this.walksCalculator.calculate(
            PitchingRankingStats.WalksAllowed, slice, session, league
        );
        const hits = this.hitsCalculator.calculate(
            PitchingRankingStats.HitsAllowed, slice, session, league
        );
        const homeRuns = this.hitsCalculator.calculate(
            PitchingRankingStats.HomeRunsAllowed, slice, session, league
        );

        const strikeoutsValue = (!strikeouts || strikeouts.isEmpty()) ? 0 : strikeouts.value;
        const walksValue = (!walks || walks.isEmpty()) ? 0 : walks.value;
        const hitsValue = (!hits || hits.isEmpty()) ? 0 : hits.value;
        const homeRunsValue = (!homeRuns || homeRuns.isEmpty()) ? 0 : homeRuns.value;

        // outs pitched, intermediate stat
        const outsPitchedParam = this.supportedParams[0];
        output[outsPitchedParam.key] = RankingStat.fromParameter(
            outsPitchedParam, this.numberFormatter.format(outsPitched), outsPitched
        );

        // @see https://www.mlb.com/glossary/standard-stats/innings-pitched and @see https://baseball.fandom.com/wiki/Innings_pitched
        // In baseball, innings pitched (IP) are the number of innings a pitcher has completed, measured by the number of batters and baserunners that are put out while the pitcher is in the game.
        // Three outs made is equal to one inning pitched. One out counts as one-third of an inning, and two outs counts as two-thirds of an inning.
        const inningsPitched = outsPitched / 3;
        const inningsPitchedParam = this.supportedParams[1];
        output[inningsPitchedParam.key] = RankingStat.fromParameter(
            inningsPitchedParam, this.numberFormatter.format(inningsPitched), outsPitched
        );

        // strikeouts per 9 innings pitched
        // @see https://baseball.fandom.com/wiki/Strikeouts_per_9_innings_pitched @see https://www.mlb.com/glossary/advanced-stats/strikeouts-per-nine-innings
        // In baseball statistics, strikeouts per 9 innings pitched (K/9IP or K/9) is the mean of strikeouts, (or K's) by a pitcher per nine innings pitched.
        // It is determined by multiplying the number of strikeouts by nine and dividing by the number of innings pitched.
        const k9Param = this.supportedParams[2];
        output[k9Param.key] = RankingStat.fromParameter(
            k9Param, this.numberFormatter.format((9 * strikeoutsValue) / inningsPitched), outsPitched // TODO - we actually use outsPitched here as it is integer, it shouldn't matter though, as innings pitched = outsPitched / 3
        );

        // walks per 9 innings pitched
        // @see https://www.mlb.com/glossary/advanced-stats/walks-per-nine-innings @see https://en.wikipedia.org/wiki/Bases_on_balls_per_nine_innings_pitched
        // In baseball statistics, bases on balls per nine innings pitched (BB/9IP or BB/9) or walks per nine innings (denoted by W/9)
        // is the average number of bases on balls, (or walks) given up by a pitcher per nine innings pitched.
        // It is determined by multiplying the number of bases on balls allowed by nine, and dividing by the number of innings pitched
        const bb9Param = this.supportedParams[3];
        output[bb9Param.key] = RankingStat.fromParameter(
            bb9Param, this.numberFormatter.format((9 * walksValue) / inningsPitched), outsPitched // TODO - we actually use outsPitched here as it is integer, it shouldn't matter though, as innings pitched = outsPitched / 3
        );

        // home runs per 9 innings pitched
        // @see https://www.mlb.com/glossary/advanced-stats/home-runs-per-nine-innings @see https://en.wikipedia.org/wiki/Home_runs_per_nine_innings
        // In baseball statistics, home runs allowed per 9 innings pitched (HR/9IP or HR/9) or home runs allowed per nine innings (denoted by HR/9)
        // is the average number of home runs given up by a pitcher per nine innings pitched.
        // It is determined by multiplying the number of home runs allowed by nine and dividing by the number of innings pitched.
        const hr9Param = this.supportedParams[4];
        output[hr9Param.key] = RankingStat.fromParameter(
            hr9Param, this.numberFormatter.format((9 * homeRunsValue) / inningsPitched), outsPitched // TODO - we actually use outsPitched here as it is integer, it shouldn't matter though, as innings pitched = outsPitched / 3
        );
        
        // hits per 9 innings pitched
        // @see https://www.mlb.com/glossary/advanced-stats/hits-per-nine-innings @see https://en.wikipedia.org/wiki/Hits_per_nine_innings
        // In baseball statistics, hits per nine innings (denoted by H/9) is the average number of hits allowed by a pitcher in a nine inning period;
        // calculated as: (hits allowed x 9) / innings pitched.[1]
        // This is a measure of a pitcher's success based on the number of all outs he records.
        const h9Param = this.supportedParams[5];
        output[h9Param.key] = RankingStat.fromParameter(
            h9Param, this.numberFormatter.format((9 * hitsValue) / inningsPitched), outsPitched // TODO - we actually use outsPitched here as it is integer, it shouldn't matter though, as innings pitched = outsPitched / 3
        );

        // Walks And Hits Per Inning Pitched (WHIP)
        // @see https://www.mlb.com/glossary/standard-stats/walks-and-hits-per-inning-pitched @see https://baseball.fandom.com/wiki/Walks_plus_hits_per_inning_pitched
        // In baseball statistics, Walks plus hits per inning pitched (WHIP) is a sabermetric measurement of the number of baserunners a pitcher has allowed per inning pitched.
        // It is a general measure of a pitcher's ability to prevent batters from reaching base.
        const whipParam = this.supportedParams[6];
        output[whipParam.key] = RankingStat.fromParameter(
            whipParam, this.numberFormatter.format((walksValue + hitsValue) / inningsPitched), outsPitched // TODO - we actually use outsPitched here as it is integer, it shouldn't matter though, as innings pitched = outsPitched / 3
        );

        return output;
    }
}

export default NewInningsPitchedCalculator;